import React, { useState } from 'react';
import UrgencyIcon from '../../atoms/urgency/urgency';
import { urgencyLevels } from '../../../constants';
import { useTranslation } from 'localization';
import './MultiUrgencyPillMolecule.scss';

/**
 * Multi-urgency pill component that displays aggregated urgency across multiple perspectives.
 * Allows users to view and modify urgency levels for each perspective individually.
 *
 * @param perspectives - Array of perspective urgency data including edge IDs and urgency levels
 * @param aggregatedUrgency - The highest/aggregated urgency value across all perspectives
 * @param onUrgencyChange - Callback triggered when a perspective's urgency is changed
 */
export interface PerspectiveUrgency {
  edgeId: string;
  perspectiveName: string;
  urgency: number;
}

interface MultiUrgencyPillMoleculeProps {
  perspectives: PerspectiveUrgency[];
  aggregatedUrgency: number;
  onUrgencyChange: (urgency: string, edgeId: string) => void;
}

const MultiUrgencyPillMolecule: React.FC<MultiUrgencyPillMoleculeProps> = ({
  perspectives,
  aggregatedUrgency,
  onUrgencyChange,
}) => {
  const { t } = useTranslation();
  const [expandedPerspective, setExpandedPerspective] = useState<string | null>(null);

  const getUrgencyLabel = (value: number): string => {
    const level = urgencyLevels.find((l) => l.value === value);
    return level?.label || t('urgency.unknown');
  };

  const handleUrgencySelect = (urgency: number, edgeId: string) => {
    onUrgencyChange(urgency.toString(), edgeId);
    setExpandedPerspective(null);
  };

  const togglePerspectiveDropdown = (edgeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedPerspective(expandedPerspective === edgeId ? null : edgeId);
  };

  return (
    <div className="multi-urgency-pill">
      {/* Header showing aggregated urgency */}
      <div className="multi-urgency-header">
        <span className="header-label">{t('urgency.highestPriority')}</span>
        <span className="header-value">
          <UrgencyIcon urgencyValue={aggregatedUrgency} />
          <span className="urgency-text">{getUrgencyLabel(aggregatedUrgency)}</span>
        </span>
      </div>

      <div className="multi-urgency-divider" />

      {/* Individual perspective urgencies */}
      <div className="multi-urgency-perspectives">
        {perspectives.map((perspective) => (
          <div key={perspective.edgeId} className="perspective-row">
            <span className="perspective-name">{perspective.perspectiveName}</span>
            <button
              type="button"
              className="perspective-urgency-selector"
              onClick={(e) => togglePerspectiveDropdown(perspective.edgeId, e as unknown as React.MouseEvent)}
            >
              <UrgencyIcon urgencyValue={perspective.urgency} />
              <span className="urgency-value">{perspective.urgency}</span>
              <i className="pi pi-chevron-down dropdown-icon" />

              {/* Dropdown menu for selecting urgency */}
              {expandedPerspective === perspective.edgeId && (
                <div className="urgency-dropdown" role="menu" aria-label="Urgency options">
                  {urgencyLevels.map((level) => (
                    <button
                      type="button"
                      key={level.value}
                      className={`urgency-option ${level.value === perspective.urgency ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUrgencySelect(level.value, perspective.edgeId);
                      }}
                      role="menuitem"
                      aria-current={level.value === perspective.urgency ? 'true' : undefined}
                    >
                      <UrgencyIcon urgencyValue={level.value} />
                      <span>{level.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </button>
          </div>
        ))}
      </div>

    </div>
  );
};

export default MultiUrgencyPillMolecule;
